import React, { useMemo } from 'react';

import { TableCell, TableRow, TableRowActionsMenu } from '@occc/ui-design-system';
import { KioskAssetFragment } from '@occc/graphql';

import { ThumbnailImage } from '../../components/thumbnail-image';
import { TABLE_CELL_TRUNCATE_STYLES, TABLE_CELL_IMAGE_STYLES } from '../../touchpoint.constants';

type AssetsListTableRowProps = {
  asset: KioskAssetFragment;
  actions: {
    onItemClick: (assetId: string) => void;
    onView: (assetId: string) => void;
    onEdit: (assetId: string) => void;
    onDelete: (assetId: string) => void;
  };
  permissions: {
    canEdit: boolean;
    canView: boolean;
    canDelete: boolean;
  };
  isAdminUser: boolean;
};

const AssetsListTableRowComponent = ({
  asset,
  actions,
  permissions,
  isAdminUser,
}: AssetsListTableRowProps) => {
  const { id, name, description, imageSet } = asset;

  // Use square image for thumbnail
  const thumbnailImageUrl = imageSet?.square;

  const menuItems = useMemo(() => {
    const items = [];

    if (permissions.canView) {
      items.push({
        label: 'View',
        onClick: () => actions.onView(id),
        dataTestId: 'view',
      });
    }

    if (permissions.canEdit) {
      items.push({
        label: 'Edit',
        onClick: () => actions.onEdit(id),
        dataTestId: 'edit',
      });
    }

    if (permissions.canDelete) {
      items.push({
        label: 'Delete',
        onClick: () => actions.onDelete(id),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [actions, permissions, id]);

  const onRowClick = () => {
    actions.onItemClick(id);
  };

  return (
    <TableRow onClick={onRowClick}>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{name || ''}</TableCell>
      <TableCell sx={TABLE_CELL_TRUNCATE_STYLES}>{description || ''}</TableCell>
      <TableCell sx={TABLE_CELL_IMAGE_STYLES}>
        <ThumbnailImage
          imageUrl={thumbnailImageUrl}
          altText={name || 'Asset thumbnail'}
          size={40}
        />
      </TableCell>
      {isAdminUser && (
        <TableCell padding='button' align='right'>
          <TableRowActionsMenu menuItems={menuItems} />
        </TableCell>
      )}
    </TableRow>
  );
};

export const AssetsListTableRow = React.memo<AssetsListTableRowProps>(AssetsListTableRowComponent);
